import Phaser from 'phaser'
import { BootScene } from './scenes/BootScene'
import { MenuScene } from './scenes/MenuScene'
import { GameScene } from './scenes/GameScene'
import { GameOverScene } from './scenes/GameOverScene'

export type Difficulty = 'easy' | 'medium' | 'hard'

export interface GameSettings {
  difficulty: Difficulty
  soundEnabled: boolean
}

export const DIFFICULTY_CONFIG = {
  easy: {
    gridWidth: 7,
    startingRows: 4,
    pearlColors: 4,
    descentSpeed: 0, // No descent on easy
    label: 'Easy (Ages 5-6)'
  },
  medium: {
    gridWidth: 9,
    startingRows: 5,
    pearlColors: 5,
    descentSpeed: 0.02, // Slow descent
    label: 'Medium (Ages 7-8)'
  },
  hard: {
    gridWidth: 11,
    startingRows: 6,
    pearlColors: 6,
    descentSpeed: 0.05, // Faster descent
    label: 'Hard (Ages 9-10)'
  }
}

// Thea's Games Color Palette
export const COLORS = {
  lightTeal: 0x5DD3C3,
  mediumTeal: 0x40B5A8,
  darkNavy: 0x1E3A5F,
  mediumPurple: 0x8B6BB5,
  deepPurple: 0x6B4D94,
  sandyCoral: 0xF4A460,
  brightCoral: 0xFF9966,
  paleAqua: 0x98E8DC,
  iceAqua: 0xB8F0E8,
  white: 0xFFFFFF
}

// Pearl color definitions - VERY distinct colors for easy identification
export const PEARL_COLORS = [
  { name: 'teal', hex: 0x5DE8E0, cssHex: '#5DE8E0' },       // Light Turquoise - lighter teal
  { name: 'purple', hex: 0x9370DB, cssHex: '#9370DB' },     // Medium Purple
  { name: 'yellow', hex: 0xFFD700, cssHex: '#FFD700' },     // Gold/Yellow - very bright
  { name: 'pink', hex: 0xFF69B4, cssHex: '#FF69B4' },       // Hot Pink
  { name: 'orange', hex: 0xFFA366, cssHex: '#FFA366' },     // Light Peachy Orange
  { name: 'green', hex: 0x32CD32, cssHex: '#32CD32' }       // Lime Green (seaweed-like)
]

// Pearl physics and sizing
export const PEARL_RADIUS = 18
export const PEARL_DIAMETER = PEARL_RADIUS * 2

// Hexagonal grid offset
export const HEX_OFFSET = PEARL_RADIUS * Math.sqrt(3) // ~31.18

export const createGameConfig = (
  onGameOver?: (score: number, pearls: number) => void,
  difficulty: Difficulty = 'easy'
): Phaser.Types.Core.GameConfig => {
  return {
    type: Phaser.AUTO,
    width: 600,
    height: 700,
    backgroundColor: COLORS.darkNavy,
    physics: {
      default: 'arcade',
      arcade: {
        gravity: { x: 0, y: 0 },
        debug: false
      }
    },
    scene: [BootScene, MenuScene, GameScene, GameOverScene],
    callbacks: {
      preBoot: (game) => {
        // Store callbacks in registry for scenes to access
        game.registry.set('onGameOver', onGameOver)
        game.registry.set('difficulty', difficulty)
      }
    }
  }
}
